import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { type AllianceTaskOptionId, type AllianceTaskPeriodClient } from '../../common/allianceApi'
import {
  ALLIANCE_TASK_OPTION_META,
  ALLIANCE_TASK_TITLE_STYLE,
  ALLIANCE_TASK_VOTE_ICON,
  allianceTaskGovernanceDelta,
} from '../../common/allianceTaskOptionsClient'
import { AllianceApsTargetModal } from './AllianceApsTargetModal'
import { AllianceAttackPlanModal } from './AllianceAttackPlanModal'
import { AllianceDiplomacyLevelModal } from './AllianceDiplomacyLevelModal'
import { AllianceTechTargetModal } from './AllianceTechTargetModal'

interface AllianceTaskResultModalProps {
  open: boolean
  address: string
  /** The leader's own alliance id — used to load members for the conquer/raid attacker picker. */
  allianceId?: string
  period: AllianceTaskPeriodClient
  onFinalized: () => void
}

const BASE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal'
const MODAL_BG = `${BASE}/modal-bg.webp`
const CONFIRM_BTN = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/confirm-btn.webp'
const BACK_BTN = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/back-btn.webp'
/** Per-rank card art: card1 (green, #1) … card5 (red, last). */
const CARD_BG_BY_RANK = [
  `${BASE}/card1-bg.webp`,
  `${BASE}/card2-bg.webp`,
  `${BASE}/card3-bg.webp`,
  `${BASE}/card4-bg.webp`,
  `${BASE}/card5-bg.webp`,
]
const CINZEL = '"Cinzel", "Times New Roman", serif'

/** Dark, parchment-readable tier styling by rank (matches the governance tiers). */
function tierInfo(rank: number): { color: string; key: 'top' | 'mid' | 'low' } {
  if (rank <= 0) return { color: '#2f6a3f', key: 'top' }
  if (rank <= 2) return { color: '#2c5a7a', key: 'mid' }
  return { color: '#8a3320', key: 'low' }
}

/** i18n key + English fallback for the completion-governance line, per option. */
const COMPLETION_GOV_TEXT: Record<AllianceTaskOptionId, { key: string; fallback: string }> = {
  aps: { key: 'alliance.govDoneAps', fallback: '+5 / +10 / +20 on completion (for a 10% / 20% / 30% APS growth target).' },
  diplomacy: { key: 'alliance.govDoneDiplomacy', fallback: '+5 / +10 / +20 on completion (reach your target tier with 1 / 2 / 3 alliances).' },
  conquer: { key: 'alliance.govDoneConquer', fallback: '+5 / +10 / +20 on completion (for 2 / 4 / 6 castles conquered).' },
  raid: { key: 'alliance.govDoneRaid', fallback: '+5 / +10 / +20 on completion (for 5 / 10 / 15 castles raided).' },
  technology: { key: 'alliance.govDoneTech', fallback: '+10 on completion (advance the chosen technology).' },
}

/**
 * Governance-impact confirmation. Shown when the leader taps an option card: it explains how
 * locking THIS option changes the alliance governance score (pick-popularity delta now, the
 * completion reward, and the incomplete penalty). Confirm proceeds to the option's setting modal.
 */
const GovernanceConfirmModal: React.FC<{
  optionId: AllianceTaskOptionId
  govDelta: number
  onBack: () => void
  onConfirm: () => void
}> = ({ optionId, govDelta, onBack, onConfirm }) => {
  const { t } = useTranslation()
  const meta = ALLIANCE_TASK_OPTION_META[optionId]

  const pickColor = govDelta > 0 ? '#2f6a3f' : govDelta < 0 ? '#8a3320' : '#6a5236'
  const pickText =
    govDelta > 0
      ? t('alliance.govPickTop', { defaultValue: 'Most-voted option — governance +5 for locking it now.' })
      : govDelta < 0
        ? t('alliance.govPickLow', { defaultValue: 'A less-popular pick — governance −5 for locking it now.' })
        : t('alliance.govPickMid', { defaultValue: 'A supported option — no governance change for locking it.' })

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '2cqw',
    padding: '2.4cqw 3cqw',
    borderRadius: '1.6cqw',
    background: 'rgba(60,40,15,0.07)',
    border: '0.3cqw solid rgba(92,62,22,0.35)',
  }
  const labelStyle: React.CSSProperties = {
    fontFamily: CINZEL,
    fontWeight: 800,
    fontSize: '3cqw',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    marginTop: '0.2cqw',
  }
  const bodyStyle: React.CSSProperties = { fontFamily: CINZEL, fontWeight: 600, fontSize: '3.3cqw', lineHeight: 1.32, color: '#4a3418' }

  return createPortal(
    <div
      className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/80 p-4"
      style={{ zIndex: 235 }}
      role="presentation"
      onClick={onBack}
    >
      <div
        className="relative my-auto w-full select-none"
        style={{
          width: 'min(96vw, 560px)',
          containerType: 'inline-size',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding: '2.5% 3% 2.5%',
          filter: 'drop-shadow(0 18px 48px rgba(0,0,0,0.6))',
        }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={ALLIANCE_TASK_VOTE_ICON[optionId]}
          alt=""
          draggable={false}
          className="mx-auto block object-contain"
          style={{ height: '20cqw', width: 'auto', filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.5))' }}
        />
        <h2 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '6.6cqw', marginTop: '1.5cqw' }}>
          {t(meta.i18nKey, { defaultValue: meta.fallback })}
        </h2>
        <p
          className="text-center"
          style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '3.3cqw', color: '#5e4327', margin: '3cqw 0 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          {t('alliance.govImpactHeading', { defaultValue: 'Impact on Governance Score' })}
        </p>

        <div className="flex flex-col" style={{ gap: '2cqw', marginTop: '2.6cqw' }}>
          {/* Pick-popularity impact (immediate) */}
          <div style={rowStyle}>
            <span style={{ ...labelStyle, color: pickColor }}>{t('alliance.govNow', { defaultValue: 'Now' })}</span>
            <span style={bodyStyle}>{pickText}</span>
          </div>
          {/* Completion reward */}
          <div style={rowStyle}>
            <span style={{ ...labelStyle, color: '#2f6a3f' }}>{t('alliance.govDone', { defaultValue: 'Done' })}</span>
            <span style={bodyStyle}>{t(COMPLETION_GOV_TEXT[optionId].key, { defaultValue: COMPLETION_GOV_TEXT[optionId].fallback })}</span>
          </div>
          {/* Incomplete penalty */}
          <div style={rowStyle}>
            <span style={{ ...labelStyle, color: '#8a3320' }}>{t('alliance.govFail', { defaultValue: 'Fail' })}</span>
            <span style={bodyStyle}>{t('alliance.govFailText', { defaultValue: '−10 governance if the task is not completed by season end.' })}</span>
          </div>
        </div>

        <div className="flex items-stretch" style={{ marginTop: '5cqw', gap: '3cqw' }}>
          <button
            type="button"
            onClick={onBack}
            className="uppercase tracking-wide transition-[filter,transform] hover:brightness-110 active:scale-[0.99]"
            style={{
              width: '32%',
              height: '12cqw',
              backgroundImage: `url(${BACK_BTN})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontFamily: CINZEL,
              fontWeight: 800,
              fontSize: '3.1cqw',
              color: '#f3ead0',
              textShadow: '0 1px 2px rgba(30,15,4,0.85)',
            }}
          >
            {t('common.back', { defaultValue: 'Back' })}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 uppercase tracking-wide transition-[filter,transform] hover:brightness-110 active:scale-[0.99]"
            style={{
              height: '12cqw',
              backgroundImage: `url(${CONFIRM_BTN})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontFamily: CINZEL,
              fontWeight: 800,
              fontSize: '3.3cqw',
              letterSpacing: '0.04em',
              color: '#fff2c8',
              textShadow: '0 1px 2px rgba(30,15,4,0.9)',
            }}
          >
            {t('alliance.govConfirmContinue', { defaultValue: 'Confirm & Continue' })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Leader result modal (season close). Shows the closed season's vote tally — options sorted by
 * votes on per-rank parchment cards. Tapping a card opens a governance-impact confirmation; on
 * confirm, the option's setting modal (APS / Diplomacy / Conquer / Raid / Technology) opens.
 * Miss the finalize window and the season gets no task (governance −20). Art: assets/alliance-task.
 */
export const AllianceTaskResultModal: React.FC<AllianceTaskResultModalProps> = ({
  open,
  address,
  allianceId,
  period,
  onFinalized,
}) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<AllianceTaskOptionId | null>(null)
  const [pending, setPending] = useState<AllianceTaskOptionId | null>(null)
  const [apsOpen, setApsOpen] = useState(false)
  const [attackOpen, setAttackOpen] = useState(false)
  const [diplomacyOpen, setDiplomacyOpen] = useState(false)
  const [techOpen, setTechOpen] = useState(false)

  // Countdown to the finalize deadline — miss it and the season gets no task (governance −20).
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])
  const deadlineLeft = period.finalizeEndsAt ? new Date(period.finalizeEndsAt).getTime() - nowMs : 0
  const deadlineLabel = (() => {
    if (deadlineLeft <= 0) return '0:00'
    const s = Math.floor(deadlineLeft / 1000)
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return h > 0 ? `${h}h ${m}m` : `${m}:${String(sec).padStart(2, '0')}`
  })()

  // The deadline passed: the season is now "no task" (−20) server-side. Dismiss the forced modal
  // immediately rather than waiting for the next status poll (it has no close button).
  const dismissedRef = useRef(false)
  useEffect(() => {
    if (open && period.finalizeEndsAt && deadlineLeft <= 0 && !dismissedRef.current) {
      dismissedRef.current = true
      onFinalized()
    }
  }, [open, deadlineLeft, period.finalizeEndsAt, onFinalized])

  // Options sorted by votes (desc). `rank` from the server already encodes the tie-broken order.
  const ranked = useMemo(() => [...period.options].sort((a, b) => a.rank - b.rank), [period.options])

  const pendingDelta = useMemo(() => {
    if (!pending) return 0
    const rank = ranked.find((o) => o.id === pending)?.rank ?? 0
    return allianceTaskGovernanceDelta(rank)
  }, [pending, ranked])

  if (!open || typeof document === 'undefined') return null

  // Confirmed an option in the governance dialog → open its setting modal.
  const openSetting = (optionId: AllianceTaskOptionId) => {
    setSelected(optionId)
    setPending(null)
    if (optionId === 'aps') setApsOpen(true)
    else if (optionId === 'diplomacy') setDiplomacyOpen(true)
    else if (optionId === 'conquer' || optionId === 'raid') setAttackOpen(true)
    else if (optionId === 'technology') setTechOpen(true)
  }

  return createPortal(
    <div className="fixed inset-0 z-[230] flex items-start justify-center overflow-y-auto bg-black/80 p-4" style={{ zIndex: 230 }} role="presentation">
      <div
        className="relative my-auto w-full select-none"
        style={{
          width: 'min(96vw, 600px)',
          containerType: 'inline-size',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding: '3% 3.5% 3%',
          filter: 'drop-shadow(0 18px 48px rgba(0,0,0,0.6))',
        }}
        role="dialog"
        aria-modal="true"
      >
        <style>{`
          .atr-opt-card { transition: transform 0.16s cubic-bezier(.2,.85,.3,1), filter 0.16s ease; cursor: pointer; will-change: transform; }
          .atr-opt-card:hover { transform: translateY(-0.6cqw) scale(1.03); filter: brightness(1.07) drop-shadow(0 0.8cqw 1.6cqw rgba(0,0,0,0.4)); z-index: 3; }
          .atr-opt-card:active { transform: scale(0.965); filter: brightness(1.12); transition-duration: 0.07s; }
          .atr-opt-card:focus-visible { outline: none; transform: scale(1.02); filter: brightness(1.06); }
        `}</style>

        {/* Title */}
        <h2 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '6.6cqw' }}>
          {t('alliance.taskResultTitle', { defaultValue: 'Weekly Vote Results' })}
        </h2>
        <p className="text-center" style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '2.7cqw', lineHeight: 1.3, color: '#5e4327', margin: '1.4cqw 0 0' }}>
          {t('alliance.taskResultSubtitlePick', { defaultValue: 'Voting is closed. Tap an option to set it as this season’s official task.' })}
        </p>
        <p className="text-center" style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.7cqw', margin: '0.6cqw 0 0', color: deadlineLeft <= 60000 ? '#9a2f1a' : '#7a5a1e' }}>
          {t('alliance.taskFinalizeDeadline', {
            defaultValue: 'Set within {{time}} or the season gets no task (−20 governance)',
            time: deadlineLabel,
          })}
        </p>

        {/* Ranked cards */}
        <div className="flex w-full flex-col" style={{ gap: '2.6cqw', marginTop: '4cqw' }}>
          {ranked.map((o) => {
            const meta = ALLIANCE_TASK_OPTION_META[o.id]
            const tier = tierInfo(o.rank)
            // Card art keys off the competition rank so tied options share the same background.
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => setPending(o.id)}
                className="atr-opt-card relative flex items-center"
                style={{
                  width: '100%',
                  height: '20cqw',
                  backgroundImage: `url(${CARD_BG_BY_RANK[Math.min(o.rank, 4)]})`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  padding: '0 4.5% 0 3%',
                  overflow: 'visible',
                  flexShrink: 0,
                }}
              >
                {/* Medallion icon — taller than the card, overlapping it. */}
              <img
                  src={ALLIANCE_TASK_VOTE_ICON[o.id]}
                  alt=""
                  draggable={false}
                  className="shrink-0 object-contain"
                  style={{ height: '22cqw', width: 'auto', filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.5))', zIndex: 2 }}
                />

                <span className="min-w-0 flex-1 text-left" style={{ marginLeft: '3cqw' }}>
                  <span className="block truncate" style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '4.8cqw', lineHeight: 1.05, color: '#3a2010' }}>
                    {t(meta.i18nKey, { defaultValue: meta.fallback })}
                  </span>
                  <span className="block" style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: '2.7cqw', lineHeight: 1.15, color: tier.color, marginTop: '0.4cqw' }}>
                    {tier.key === 'top'
                      ? t('alliance.taskTierTop', { defaultValue: 'Most voted' })
                      : tier.key === 'low'
                        ? t('alliance.taskTierLow', { defaultValue: 'Unpopular (governance −5)' })
                        : t('alliance.taskTierMid', { defaultValue: 'Supported' })}
                  </span>
                </span>

                <span className="flex shrink-0 flex-col items-end" style={{ minWidth: '11cqw' }}>
                  <span style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '5.4cqw', lineHeight: 1, color: tier.color }}>{o.votes}</span>
                  <span style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '2.4cqw', color: '#6a5236' }}>
                    {t('alliance.taskVotesLabel', { defaultValue: 'votes' })}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Governance-impact confirmation (tap a card → explains impact → Confirm opens the setting modal) */}
      {pending ? (
        <GovernanceConfirmModal
          optionId={pending}
          govDelta={pendingDelta}
          onBack={() => setPending(null)}
          onConfirm={() => openSetting(pending)}
        />
      ) : null}

      {apsOpen ? <AllianceApsTargetModal open address={address} period={period} onBack={() => setApsOpen(false)} onFinalized={onFinalized} /> : null}
      {diplomacyOpen ? <AllianceDiplomacyLevelModal open address={address} allianceId={allianceId} period={period} onBack={() => setDiplomacyOpen(false)} onFinalized={onFinalized} /> : null}
      {techOpen ? <AllianceTechTargetModal open address={address} period={period} onBack={() => setTechOpen(false)} onFinalized={onFinalized} /> : null}
      {attackOpen && (selected === 'conquer' || selected === 'raid') ? (
        <AllianceAttackPlanModal
          open
          address={address}
          allianceId={allianceId}
          optionId={selected}
          period={period}
          onBack={() => setAttackOpen(false)}
          onFinalized={onFinalized}
        />
      ) : null}
    </div>,
    document.body,
  )
}

export default AllianceTaskResultModal
